import { useRef, useEffect } from "react";

const GardenVisualizer = ({ plan }) => {
  const canvasRef = useRef(null);

  const layerColors = {
    canopy: "#2C4A3B",
    understory: "#3D6B4F",
    shrub: "#4CAF50",
    herbaceous: "#8BC34A",
    ground_cover: "#AED581",
    vine: "#81C784",
    root: "#A1887F"
  };

  useEffect(() => {
    if (!canvasRef.current || !plan) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.fillStyle = "#F5F5F0";
    ctx.fillRect(0, 0, width, height);

    // Draw grid
    ctx.strokeStyle = "rgba(44, 74, 59, 0.1)";
    ctx.lineWidth = 1;
    const gridSize = 20;
    
    for (let x = 0; x <= width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    
    for (let y = 0; y <= height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Calculate scale
    const scaleX = (width - 60) / plan.width;
    const scaleY = (height - 60) / plan.length;
    const scale = Math.min(scaleX, scaleY);
    const offsetX = (width - plan.width * scale) / 2;
    const offsetY = (height - plan.length * scale) / 2;

    // Draw garden boundary
    ctx.strokeStyle = "#2C4A3B";
    ctx.lineWidth = 3;
    ctx.strokeRect(offsetX, offsetY, plan.width * scale, plan.length * scale);

    // Draw zones if available
    if (plan.zones?.length > 0) {
      plan.zones.forEach((zone, index) => {
        const x = offsetX + zone.x * scale;
        const y = offsetY + zone.y * scale;
        const w = zone.width * scale;
        const h = zone.height * scale;

        ctx.fillStyle = layerColors[zone.layer] || "#888";
        ctx.globalAlpha = 0.4;
        ctx.fillRect(x, y, w, h);
        ctx.globalAlpha = 1;

        ctx.strokeStyle = layerColors[zone.layer] || "#888";
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, w, h);

        // Zone label
        ctx.fillStyle = "#2C2C2C";
        ctx.font = "12px Manrope";
        ctx.textAlign = "center";
        ctx.fillText(zone.name, x + w / 2, y + h / 2);
      });
    } else {
      // Default zone visualization based on layers
      const layers = ["canopy", "understory", "shrub", "herbaceous", "ground_cover"];
      const layerHeight = (plan.length * scale) / layers.length;

      layers.forEach((layer, i) => {
        const y = offsetY + i * layerHeight;
        ctx.fillStyle = layerColors[layer];
        ctx.globalAlpha = 0.3;
        ctx.fillRect(offsetX, y, plan.width * scale, layerHeight);
        ctx.globalAlpha = 1;

        // Layer label
        ctx.fillStyle = "#2C2C2C";
        ctx.font = "11px Manrope";
        ctx.textAlign = "left";
        ctx.fillText(layer.replace('_', ' '), offsetX + 10, y + layerHeight / 2 + 4);
      });
    }

    // Draw compass
    const compassX = width - 40;
    const compassY = 40;
    ctx.beginPath();
    ctx.arc(compassX, compassY, 20, 0, Math.PI * 2);
    ctx.fillStyle = "white";
    ctx.fill();
    ctx.strokeStyle = "#2C4A3B";
    ctx.lineWidth = 2;
    ctx.stroke();

    // N arrow
    ctx.beginPath();
    ctx.moveTo(compassX, compassY - 15);
    ctx.lineTo(compassX - 5, compassY);
    ctx.lineTo(compassX + 5, compassY);
    ctx.closePath();
    ctx.fillStyle = "#D66A48";
    ctx.fill();

    ctx.fillStyle = "#2C4A3B";
    ctx.font = "bold 10px Space Mono";
    ctx.textAlign = "center";
    ctx.fillText("N", compassX, compassY - 5);

    // Scale indicator
    ctx.fillStyle = "#2C4A3B";
    ctx.font = "11px Space Mono";
    ctx.textAlign = "left";
    ctx.fillText(`Scale: 1m = ${Math.round(scale)}px`, 10, height - 10);

    // Dimensions
    ctx.fillText(`${plan.width}m × ${plan.length}m × ${plan.height}m`, 10, 20);

  }, [plan]);

  return (
    <div className="garden-canvas rounded-xl p-4">
      <canvas
        ref={canvasRef}
        width={600}
        height={400}
        className="w-full h-auto rounded-lg border border-border bg-white"
        data-testid="garden-canvas"
      />
      
      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-2 justify-center">
        {Object.entries(layerColors).map(([layer, color]) => (
          <div key={layer} className="flex items-center gap-1.5 text-xs">
            <div 
              className="w-4 h-4 rounded" 
              style={{ backgroundColor: color, opacity: 0.6 }}
            />
            <span className="capitalize text-muted-foreground">
              {layer.replace('_', ' ')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GardenVisualizer;
